import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import { useTransactions } from "@/hooks/useTransactions";
import { useMemo, useState } from "react";
import { Button } from "./ui/button";
import { ChevronRight, ChevronLeft, ExpandIcon } from "lucide-react";
import { useBankTransactions } from "@/hooks/useBankTransactions";
import { useDashboardRange } from "./DashboardContext";
import { useAnonymize, anonymizeCategory, anonymizeAmount } from "@/components/AnonymizeContext";

function isDateInCurrentPeriod(date: string, range: "month" | "year") {
  if (!date) return false;
  const now = new Date();
  const d = new Date(date);
  if (range === "month") {
    return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
  } else {
    return d.getFullYear() === now.getFullYear();
  }
}

const COLORS = [
  "#9b87f5", "#D6BCFA", "#7E69AB", "#8B5CF6", "#D946EF", "#F97316", "#0EA5E9", "#F2FCE2", "#FEF7CD", "#FEC6A1", "#E5DEFF"
];

export default function SpendingPieChart() {
  const { data: bankTxns = [], isLoading: loadingBank, error: errorBank } = useBankTransactions();
  const { data: appTxns = [], isLoading: loadingApp, error: errorApp } = useTransactions();
  const [drillOther, setDrillOther] = useState(false);
  const range = useDashboardRange();
  const { anonymize } = useAnonymize();

  const isLoading = loadingBank || loadingApp;
  const error = errorBank || errorApp;

  const filteredBankTxns = useMemo(
    () => bankTxns.filter((t) => t.type === "Expense"),
    [bankTxns]
  );

  const mergedTxns = useMemo(
    () =>
      [
        ...filteredBankTxns,
        ...appTxns,
      ].filter(
        (t) =>
          isDateInCurrentPeriod(t.date, range) &&
          t.type === "Expense" &&
          t.amount < 0 &&
          t.category !== "Investment"
      ),
    [filteredBankTxns, appTxns, range]
  );

  const { chartData, legendData, otherBreakdown } = useMemo(() => {
    const grouped = mergedTxns.reduce((acc, t) => {
      const absAmount = Math.abs(t.amount);
      const name = anonymize ? anonymizeCategory(t.category, Object.keys(acc).length) : t.category;
      if (!acc[name]) acc[name] = 0;
      acc[name] += absAmount;
      return acc;
    }, {} as Record<string, number>);

    const sorted = Object.entries(grouped)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);

    const top = sorted.slice(0, 6);
    const other = sorted.slice(6);

    let pieChartData = [...top];
    let legendList = [...top];

    if (other.length > 0) {
      const totalOther = other.reduce((sum, item) => sum + item.value, 0);
      pieChartData.push({ name: "Other", value: totalOther });
      legendList.push({ name: "Other", value: totalOther });
    }

    return { chartData: pieChartData, legendData: legendList, otherBreakdown: other };
  }, [mergedTxns, anonymize]);

  const displayData = drillOther && otherBreakdown.length > 0 ? otherBreakdown : chartData;
  const displayLegend = drillOther && otherBreakdown.length > 0 ? otherBreakdown : legendData;
  const showDrill = !!otherBreakdown.length;

  if (isLoading) {
    return (
      <div className="h-64 flex items-center justify-center text-gray-400 bg-white rounded-xl shadow">
        Loading...
      </div>
    );
  }
  if (error) {
    return (
      <div className="h-64 flex items-center justify-center text-destructive bg-white rounded-xl shadow">
        Error loading pie chart.
      </div>
    );
  }
  if (!chartData.length) {
    return (
      <div className="h-64 flex items-center justify-center text-gray-400 bg-white rounded-xl shadow">
        No spending data for this {range}.
      </div>
    );
  }

  function renderLegend() {
    return (
      <div className="flex flex-col gap-2 ml-5 min-w-[120px]">
        {displayLegend.map((item, i) => (
          <div key={item.name} className="flex items-center gap-2">
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: COLORS[i % COLORS.length] }}
            />
            <span className="text-xs text-gray-700">{item.name}</span>
            <span className="text-xs text-gray-500 ml-2">
              {anonymize
                ? anonymizeAmount(item.value)
                : `$${Number(item.value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
            </span>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow p-4 relative flex flex-col sm:flex-row gap-6 items-stretch">
      <div className="absolute top-4 right-4 flex gap-2 z-10">
        {showDrill && (
          <Button
            variant="outline"
            size="sm"
            className="h-8 px-2"
            onClick={() => setDrillOther((v) => !v)}
          >
            {drillOther ? (
              <>
                <ChevronLeft className="h-4 w-4 mr-1" /> Back
              </>
            ) : (
              <>
                <ExpandIcon className="h-4 w-4 mr-1" /> Other details
              </>
            )}
          </Button>
        )}
      </div>
      <div className="flex-1 flex flex-col min-w-0">
        <h3 className="font-semibold text-lg mb-2 ml-2">Spending by Category</h3>
        <div className="flex items-center justify-center h-full">
          <ResponsiveContainer width={250} height={250}>
            <PieChart>
              <Pie
                data={displayData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={90}
                labelLine={false}
              >
                {displayData.map((entry, i) => (
                  <Cell key={entry.name} fill={COLORS[i % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip
                formatter={(value: number, name: string) => [
                  anonymize
                    ? anonymizeAmount(Number(value))
                    : `$${Number(value).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}`,
                  name,
                ]}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="flex flex-col justify-center pr-4">
        {renderLegend()}
      </div>
    </div>
  );
}
